/**
 * Securidou — Visual Reviews UGC Component V2
 * Stories-like experience: auto-play on visible, carousel dots, fullscreen nav.
 * Scroll-reveal handled globally by animations.js (sd-reveal / sd-reveal-stagger).
 * Loaded with defer — DOM is ready when this runs.
 */
(function () {
  'use strict';

  var reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  /* ─── PER-SECTION INITIALIZATION ─────────────────── */
  document.querySelectorAll('[data-ugc-section]').forEach(function (section) {
    var sectionId = section.dataset.ugcSection;
    var modal = document.querySelector('[data-ugc-modal="' + sectionId + '"]');
    var fsVideo = document.getElementById('ugc-fsvid-' + sectionId);
    var closeBtn = modal ? modal.querySelector('.ugc-fs-close') : null;
    var cards = section.querySelectorAll('[data-ugc-card]');

    if (!modal || !fsVideo || !closeBtn || cards.length === 0) return;

    initLazyVideos(cards);
    initAutoPlayOnVisible(cards);
    initCarouselDots(section);
    initModal(cards, modal, fsVideo, closeBtn);
  });

  /* ─── VIDEO LAZY LOADING ─────────────────────────── */
  function initLazyVideos(cards) {
    if (!('IntersectionObserver' in window)) {
      cards.forEach(function (card) {
        var vid = card.querySelector('.ugc-vid[data-src]');
        if (vid) {
          vid.src = vid.dataset.src;
          vid.removeAttribute('data-src');
          vid.preload = 'metadata';
        }
      });
      return;
    }

    var observer = new IntersectionObserver(function (entries, obs) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        var vid = entry.target.querySelector('.ugc-vid[data-src]');
        if (vid && !vid.src) {
          vid.src = vid.dataset.src;
          vid.removeAttribute('data-src');
          vid.preload = 'metadata';
        }
        obs.unobserve(entry.target);
      });
    }, { rootMargin: '200px' });

    cards.forEach(function (card) {
      if (card.querySelector('.ugc-vid[data-src]')) {
        observer.observe(card);
      }
    });
  }

  /* ─── AUTO-PLAY ON VISIBLE (replaces hover preview) ── */
  function initAutoPlayOnVisible(cards) {
    if (reducedMotion || !('IntersectionObserver' in window)) return;

    var playObserver = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        var card = entry.target;
        var vid = card.querySelector('.ugc-vid');
        var progressFill = card.querySelector('.ugc-progress-fill');
        if (!vid) return;

        if (entry.isIntersecting && entry.intersectionRatio >= 0.6) {
          /* Wait for src to be loaded by lazy observer */
          if (!vid.src && vid.dataset.src) {
            vid.src = vid.dataset.src;
            vid.removeAttribute('data-src');
            vid.preload = 'metadata';
          }
          if (!vid.src) return;

          card.classList.add('is-playing');
          vid.play().catch(function () {});

          if (progressFill) {
            vid.addEventListener('timeupdate', function onTime() {
              if (vid.duration && card.classList.contains('is-playing')) {
                progressFill.style.width = (vid.currentTime / vid.duration * 100) + '%';
              }
            });
          }
        } else {
          card.classList.remove('is-playing');
          if (vid.src) {
            vid.pause();
          }
          if (progressFill) progressFill.style.width = '0%';
        }
      });
    }, { threshold: [0, 0.6] });

    cards.forEach(function (card) {
      if (card.classList.contains('has-video')) {
        playObserver.observe(card);
      }
    });

    /* Desktop: also keep hover preview for pointer devices */
    if (window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
      cards.forEach(function (card) {
        if (!card.classList.contains('has-video')) return;
        card.addEventListener('mouseenter', function () {
          var vid = card.querySelector('.ugc-vid');
          if (vid && vid.src) {
            card.classList.add('is-playing');
            vid.play().catch(function () {});
          }
        });
        card.addEventListener('mouseleave', function () {
          var vid = card.querySelector('.ugc-vid');
          if (vid) {
            card.classList.remove('is-playing');
            vid.pause();
            vid.currentTime = 0;
            var pf = card.querySelector('.ugc-progress-fill');
            if (pf) pf.style.width = '0%';
          }
        });
      });
    }
  }

  /* ─── CAROUSEL DOT SYNC ──────────────────────────── */
  function initCarouselDots(section) {
    var grid = section.querySelector('[data-ugc-grid]');
    var dots = section.querySelectorAll('.ugc-dot');
    if (!grid || dots.length === 0) return;

    var ticking = false;

    grid.addEventListener('scroll', function () {
      if (!ticking) {
        window.requestAnimationFrame(function () {
          var cardEls = grid.querySelectorAll('.ugc-card');
          if (cardEls.length === 0) { ticking = false; return; }

          var gap = 16;
          var cardWidth = cardEls[0].offsetWidth + gap;
          var activeIndex = Math.round(grid.scrollLeft / cardWidth);
          activeIndex = Math.max(0, Math.min(activeIndex, dots.length - 1));

          dots.forEach(function (dot, i) {
            dot.classList.toggle('active', i === activeIndex);
            dot.setAttribute('aria-current', i === activeIndex ? 'true' : 'false');
          });

          ticking = false;
        });
        ticking = true;
      }
    }, { passive: true });

    /* Dot click → scroll to card */
    dots.forEach(function (dot, index) {
      dot.addEventListener('click', function () {
        var cardEls = grid.querySelectorAll('.ugc-card');
        if (cardEls[index]) {
          cardEls[index].scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'center' });
        }
      });
    });
  }

  /* ─── FULLSCREEN MODAL WITH NAV ──────────────────── */
  function initModal(cards, modal, fsVideo, closeBtn) {
    var lastFocused = null;
    var currentIndex = 0;
    var videoCards = [];

    /* Build list of cards that have actual video */
    cards.forEach(function (card, i) {
      if (card.classList.contains('has-video')) {
        videoCards.push({ card: card, index: i });
      }
    });

    if (videoCards.length === 0) return;

    var prevBtn = modal.querySelector('.ugc-fs-prev');
    var nextBtn = modal.querySelector('.ugc-fs-next');
    var progressContainer = modal.querySelector('[data-ugc-fs-progress]');

    /* Build segmented progress bar */
    function buildProgress() {
      if (!progressContainer) return;
      progressContainer.innerHTML = '';
      videoCards.forEach(function () {
        var seg = document.createElement('div');
        seg.className = 'ugc-fs-seg';
        seg.innerHTML = '<div class="ugc-fs-seg-fill"></div>';
        progressContainer.appendChild(seg);
      });
    }

    function updateProgress(activeIdx) {
      if (!progressContainer) return;
      var segs = progressContainer.querySelectorAll('.ugc-fs-seg');
      segs.forEach(function (seg, i) {
        seg.classList.remove('done', 'current');
        var fill = seg.querySelector('.ugc-fs-seg-fill');
        if (i < activeIdx) {
          seg.classList.add('done');
          fill.style.width = '100%';
        } else if (i === activeIdx) {
          seg.classList.add('current');
          fill.style.width = '0%';
        } else {
          fill.style.width = '0%';
        }
      });
    }

    function openAtIndex(idx) {
      if (idx < 0 || idx >= videoCards.length) return;
      currentIndex = idx;
      var card = videoCards[idx].card;
      var vid = card.querySelector('.ugc-vid');
      if (!vid) return;
      var src = vid.src || vid.dataset.src;
      if (!src) return;

      /* Pause all card videos */
      cards.forEach(function (c) {
        var v = c.querySelector('.ugc-vid');
        if (v) { v.pause(); v.currentTime = 0; }
        c.classList.remove('is-playing');
      });

      /* Populate modal */
      fsVideo.poster = card.dataset.poster || '';
      fsVideo.src = src;
      fsVideo.muted = false;
      fsVideo.loop = false;
      modal.classList.add('open');
      modal.setAttribute('aria-hidden', 'false');
      document.body.style.overflow = 'hidden';
      closeBtn.focus();
      fsVideo.play().catch(function () {});
      updateProgress(idx);

      /* Animate progress fill */
      fsVideo.ontimeupdate = function () {
        var segs = progressContainer ? progressContainer.querySelectorAll('.ugc-fs-seg') : [];
        if (segs[currentIndex] && fsVideo.duration) {
          var fill = segs[currentIndex].querySelector('.ugc-fs-seg-fill');
          if (fill) fill.style.width = (fsVideo.currentTime / fsVideo.duration * 100) + '%';
        }
      };

      /* Update prev/next visibility */
      if (prevBtn) prevBtn.hidden = (idx === 0);
      if (nextBtn) nextBtn.hidden = (idx === videoCards.length - 1);
    }

    function closeModal() {
      modal.classList.remove('open');
      modal.setAttribute('aria-hidden', 'true');
      fsVideo.pause();
      fsVideo.removeAttribute('src');
      fsVideo.poster = '';
      fsVideo.ontimeupdate = null;
      document.body.style.overflow = '';
      if (lastFocused) lastFocused.focus();
    }

    buildProgress();

    /* Card click → open modal */
    cards.forEach(function (card, i) {
      if (!card.classList.contains('has-video')) return;
      var videoIdx = -1;
      for (var j = 0; j < videoCards.length; j++) {
        if (videoCards[j].index === i) { videoIdx = j; break; }
      }
      if (videoIdx === -1) return;

      card.addEventListener('click', function () {
        lastFocused = card.querySelector('.ugc-card-trigger') || card;
        openAtIndex(videoIdx);
      });
      card.addEventListener('keydown', function (e) {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          lastFocused = card.querySelector('.ugc-card-trigger') || card;
          openAtIndex(videoIdx);
        }
      });
    });

    /* Prev / Next */
    if (prevBtn) prevBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      openAtIndex(currentIndex - 1);
    });
    if (nextBtn) nextBtn.addEventListener('click', function (e) {
      e.stopPropagation();
      openAtIndex(currentIndex + 1);
    });

    /* Auto-advance on video end */
    fsVideo.addEventListener('ended', function () {
      if (currentIndex < videoCards.length - 1) {
        openAtIndex(currentIndex + 1);
      } else {
        closeModal();
      }
    });

    /* Close handlers */
    closeBtn.addEventListener('click', closeModal);
    modal.addEventListener('click', function (e) {
      if (e.target === modal) closeModal();
    });

    /* Keyboard: Escape, Arrow keys */
    document.addEventListener('keydown', function (e) {
      if (!modal.classList.contains('open')) return;
      if (e.key === 'Escape') closeModal();
      if (e.key === 'ArrowLeft' && currentIndex > 0) openAtIndex(currentIndex - 1);
      if (e.key === 'ArrowRight' && currentIndex < videoCards.length - 1) openAtIndex(currentIndex + 1);
    });

    /* Focus trap */
    var focusable = [closeBtn, prevBtn, fsVideo, nextBtn].filter(Boolean);
    modal.addEventListener('keydown', function (e) {
      if (e.key !== 'Tab' || focusable.length === 0) return;
      var visibleFocusable = focusable.filter(function (el) { return !el.hidden; });
      if (visibleFocusable.length === 0) return;
      var first = visibleFocusable[0];
      var last = visibleFocusable[visibleFocusable.length - 1];
      if (e.shiftKey) {
        if (document.activeElement === first) { e.preventDefault(); last.focus(); }
      } else {
        if (document.activeElement === last) { e.preventDefault(); first.focus(); }
      }
    });
  }
})();
